import { useParams } from "react-router-dom";
import { useEffect, useState, useCallback } from "react";
import {
  getPublicQRCodes,
  verifySecureCode,
  checkIsProtectedQRCodes,
} from "../api/qrcode";
import toast from "react-hot-toast";
import { AiOutlineEye, AiOutlineEyeInvisible } from "react-icons/ai";
import RainbowLogo from "../assets/images/rainbowrebels-logo.png";
import ForeverConnectedLogo from "../assets/images/forever-connected-logo.png";
import { IoClose } from "react-icons/io5";
const BarcodeInfo = () => {
  const { id } = useParams();

  const [processing, setProcessing] = useState(false);
  const [barcodeData, setBarcodeData] = useState(null);
  const [isPdfOpen, setIsPdfOpen] = useState(false);
  const [pdfUrl, setPdfUrl] = useState(null);
  const [activeTab, setActiveTab] = useState("about");
  // New states for secure code verification
  const [secureCode, setSecureCode] = useState("");
  const [isProtected, setIsProtected] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const [selectedImage, setSelectedImage] = useState(null);
  const [pdfLoading, setPdfLoading] = useState(true);
  const [pdfError, setPdfError] = useState(false);
  const [pdfRetryCount, setPdfRetryCount] = useState(0);
  const [iframeKey, setIframeKey] = useState(0);

  const getBarcodeData = async () => {
    setProcessing(true);
    try {
      const { data } = await getPublicQRCodes(id);
      setBarcodeData(data.barcode);
    } catch (error) {
      toast.error(
        error?.response?.data?.message || "Error while getting barcode data"
      );
    } finally {
      setProcessing(false);
    }
  };

  const handleSecureCodeSubmit = async (e) => {
    e.preventDefault();
    setProcessing(true);
    try {
      const response = await verifySecureCode(id, secureCode);
      if (response.data.verified) {
        getBarcodeData();
        setIsProtected(false);
      } else {
        toast.error("Invalid secure code");
      }
    } catch (error) {
      toast.error(
        error?.response?.data?.message || "Error verifying secure code"
      );
    } finally {
      setProcessing(false);
    }
  };

  useEffect(() => {
    const checkIsProtected = async () => {
      const response = await checkIsProtectedQRCodes(id);
      setIsProtected(response.data.isProtected);
      if (!response.data.isProtected) {
        getBarcodeData();
      }
    };
    checkIsProtected();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const changeTab = (tab) => {
    setActiveTab(tab);
  };

  // Helper function to retry PDF loading
  const retryPdfLoad = useCallback(() => {
    if (pdfRetryCount < 3) {
      setPdfRetryCount((prev) => prev + 1);
      setPdfLoading(true);
      setPdfError(false);
      setIframeKey((prev) => prev + 1); // Force iframe recreation
    } else {
      setPdfError(true);
      setPdfLoading(false);
    }
  }, [pdfRetryCount]);

  // Helper function to handle PDF modal opening
  const openPdfModal = (url) => {
    setPdfUrl(url);
    setIsPdfOpen(true);
    setPdfLoading(true);
    setPdfError(false);
    setPdfRetryCount(0);
    setIframeKey(0);
  };

  // Helper function to handle PDF modal closing
  const closePdfModal = () => {
    setIsPdfOpen(false);
    setPdfLoading(true);
    setPdfError(false);
    setPdfRetryCount(0);
    setIframeKey(0);
  };

  // Timeout handler for PDF loading
  useEffect(() => {
    if (isPdfOpen && pdfLoading && !pdfError) {
      const timer = setTimeout(() => {
        if (pdfLoading && pdfRetryCount < 3) {
          retryPdfLoad();
        } else if (pdfLoading) {
          setPdfError(true);
          setPdfLoading(false);
        }
      }, 10000); // 10 second timeout

      return () => clearTimeout(timer);
    }
  }, [isPdfOpen, pdfLoading, pdfError, pdfRetryCount, retryPdfLoad]);

  if (isProtected) {
    return (
      <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-75 z-50">
        <div className="bg-white p-8 rounded-lg max-w-md w-full">
          <h2 className="text-2xl font-bold mb-4">Enter Secure Code</h2>
          <form onSubmit={handleSecureCodeSubmit}>
            <div className="mt-4">
              <div className="relative">
                <input
                  type={showPassword ? "text" : "password"}
                  value={secureCode}
                  onChange={(e) => setSecureCode(e.target.value)}
                  className="w-full p-2 border border-gray-300 rounded mb-4"
                  placeholder="Enter secure code"
                  required
                />
                <button
                  type="button"
                  className="absolute inset-y-0 right-0 pr-3 pb-3 flex items-center text-sm leading-5"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? (
                    <AiOutlineEyeInvisible className="h-5 w-5 text-gray-500" />
                  ) : (
                    <AiOutlineEye className="h-5 w-5 text-gray-500" />
                  )}
                </button>
              </div>
            </div>
            <button
              type="submit"
              className="w-full bg-tealBlue text-white py-2 rounded hover:bg-[#006d95]"
              disabled={processing}
            >
              {processing ? "Verifying..." : "Submit"}
            </button>
          </form>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="flex flex-col min-h-screen w-full mx-auto">
        <div className="flex-grow">
          <div className=" bg-[#E7EEF5]  px-4 lg:px-16 pt-12 pb-6  md:py-14 lg:py-16">
            <div className=" max-w-[1440px] w-full mx-auto flex items-center gap-4 md:gap-8 lg:gap-12 flex-col-reverse sm:flex-row justify-center">
              <div className="flex flex-col items-center">
                {/* Title Section */}
                {barcodeData?.storedInfo?.memories?.title && (
                  <div>
                    <h1 className="text-3xl md:text-4xl lg:text-5xl font-medium capitalize text-[#2D3A45] max-w-[700px] mb-2 text-center">
                      {barcodeData?.storedInfo?.memories?.title}
                    </h1>
                  </div>
                )}

                {barcodeData?.storedInfo?.memories?.subTitle && (
                  <div>
                    <h2 className="text-xl md:text-2xl font-normal text-[#2D3A45] max-w-[700px] text-center">
                      {barcodeData?.storedInfo?.memories?.subTitle}
                    </h2>
                  </div>
                )}
              </div>
              {barcodeData?.storedInfo?.memories?.topImage && (
                <div className="space-y-2">
                  <div className="">
                    <img
                      src={barcodeData?.storedInfo?.memories?.topImage}
                      alt="topimage"
                      className="w-[160px] md:w-[200px] h-[210px] md:h-[270px] object-cover cursor-pointer rounded-md border-[6px] border-white shadow-[13px_12px_16px_rgba(0,0,0,0.15)]"
                    />
                  </div>
                </div>
              )}
            </div>
          </div>
          <div className="flex justify-center flex-wrap border-t border-b bg-[#F3F4F6] border-gray-200 pt-4 px-1">
            <button
              className={`${
                activeTab === "about"
                  ? "bg-[#4E7BBE] text-white hover:text-white rounded-t-md hover:!bg-[#4E7BBE]"
                  : "text-[#2D3A45] bg-[#F8F8F8] shadow-[1px_12px_16px_rgba(0,0,0,0.15)]"
              } py-2 px-2 sm:px-6 flex-col justify-center hover:bg-[#E7EEF5] max-w-[80px] sm:max-w-[125px] w-full text-xs sm:text-base font-semibold tracking-wider uppercase flex focus:outline-none`}
              onClick={() => changeTab("about")}
            >
              About
            </button>
            <button
              className={`${
                activeTab === "gallery"
                  ? "bg-[#4E7BBE] text-white hover:text-white rounded-t-md hover:!bg-[#4E7BBE]"
                  : "text-[#2D3A45] bg-[#F8F8F8] shadow-[1px_12px_16px_rgba(0,0,0,0.15)]"
              } py-3 px-2 sm:px-6 flex-col justify-center hover:bg-[#E7EEF5] max-w-[80px] sm:max-w-[125px] w-full text-xs sm:text-base font-semibold tracking-wider uppercase flex focus:outline-none `}
              onClick={() => changeTab("gallery")}
            >
              PHOTOS
            </button>
            <button
              className={`${
                activeTab === "videos"
                  ? "bg-[#4E7BBE] text-white hover:text-white rounded-t-md hover:!bg-[#4E7BBE]"
                  : "text-[#2D3A45] bg-[#F8F8F8] shadow-[1px_12px_16px_rgba(0,0,0,0.15)]"
              } py-2 px-2 sm:px-6 flex-col justify-center hover:bg-[#E7EEF5] max-w-[80px] sm:max-w-[125px] w-full text-xs sm:text-base font-semibold tracking-wider uppercase flex focus:outline-none`}
              onClick={() => changeTab("videos")}
            >
              Videos
            </button>
            <button
              className={`${
                activeTab === "pdf"
                  ? "bg-[#4E7BBE] text-white hover:text-white rounded-t-md hover:!bg-[#4E7BBE]"
                  : "text-[#2D3A45] bg-[#F8F8F8] shadow-[1px_12px_16px_rgba(0,0,0,0.15)]"
              } py-2 px-2 sm:px-6 flex-col justify-center hover:bg-[#E7EEF5] max-w-[80px] sm:max-w-[125px] w-full text-xs sm:text-base font-semibold tracking-wider uppercase flex focus:outline-none`}
              onClick={() => changeTab("pdf")}
            >
              STORIES
            </button>
          </div>
          <div className="max-w-[800px] overflow-hidden w-full mx-auto bg-[#F3F4F6] rounded max-h-auto overflow-y-auto py-6 px-4">
            {activeTab === "about" && (
              <div>
                {/* Free Text Section */}
                {barcodeData?.storedInfo?.memories?.freeText && (
                  <div className=" py-4 text-2xl text-center rounded-md">
                    {/<[^>]*>/g.test(
                      barcodeData.storedInfo.memories.freeText
                    ) ? (
                      <div
                        className="text-base leading-5 break-words md:text-lg text-default-800 text-left"
                        dangerouslySetInnerHTML={{
                          __html: barcodeData.storedInfo.memories.freeText,
                        }}
                      />
                    ) : (
                      <p className="text-[16px] break-words text-left">
                        {barcodeData.storedInfo.memories.freeText
                          .split(/(\s+)/)
                          .map((word, index) => {
                            const urlRegex = /^(https?:\/\/[^\s]+)$/i;
                            if (urlRegex.test(word)) {
                              return (
                                <a
                                  key={index}
                                  href={word.toLowerCase()}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-blue-600 underline"
                                >
                                  {word}
                                </a>
                              );
                            }
                            return <span key={index}>{word}</span>;
                          })}
                      </p>
                    )}
                  </div>
                )}
              </div>
            )}
            {activeTab === "gallery" && (
              <div>
                {barcodeData?.storedInfo?.memories?.imageText && (
                  <div className="text-center py-4 rounded-md">
                    {/<[^>]*>/g.test(
                      barcodeData.storedInfo.memories.imageText
                    ) ? (
                      <div
                        className="text-base leading-5 break-words md:text-lg text-default-800 text-left"
                        dangerouslySetInnerHTML={{
                          __html: barcodeData.storedInfo.memories.imageText,
                        }}
                      />
                    ) : (
                      <p className="text-[16px] break-words text-left">
                        {barcodeData.storedInfo.memories.imageText
                          .split(/(\s+)/)
                          .map((word, index) => {
                            const urlRegex = /^(https?:\/\/[^\s]+)$/i;
                            if (urlRegex.test(word)) {
                              return (
                                <a
                                  key={index}
                                  href={word.toLowerCase()}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-blue-600 underline"
                                >
                                  {word}
                                </a>
                              );
                            }
                            return <span key={index}>{word}</span>;
                          })}
                      </p>
                    )}
                  </div>
                )}
                {/* Images Carousel */}
                {barcodeData?.storedInfo?.memories?.images?.length > 0 && (
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                    {barcodeData?.storedInfo?.memories?.images?.map(
                      (image, index) => {
                        return (
                          <div
                            key={index}
                            className="overflow-hidden rounded-md h-[156px] xs:h-[245px]"
                          >
                            <img
                              src={image?.url}
                              alt={`Memory ${index + 1}`}
                              className="w-full h-full object-cover cursor-pointer rounded-md transition-transform duration-300 hover:scale-110"
                              onClick={() => setSelectedImage(image.url)}
                            />
                          </div>
                        );
                      }
                    )}{" "}
                  </div>
                )}

                {/* Image Pop-out */}
                {selectedImage && (
                  <div
                    className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-75 p-4"
                    onClick={() => setSelectedImage(null)}
                  >
                    <div className="relative flex items-center justify-center h-[90vh] w-full">
                      <div className="relative inline-block">
                        <img
                          src={selectedImage}
                          alt="Enlarged view"
                          className="max-w-full max-h-[90vh] object-contain rounded"
                        />
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedImage(null);
                          }}
                          className="absolute top-2 right-2 bg-black bg-opacity-50 text-white rounded-full text-xl w-7 h-7 flex items-center justify-center leading-none hover:bg-opacity-75 transition-opacity"
                        >
                          <IoClose />
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}

            {activeTab === "videos" && (
              <div>
                {barcodeData?.storedInfo?.memories?.videoDescription && (
                  <div className="text-center  pt-4 pb-6">
                    {/<[^>]*>/g.test(
                      barcodeData.storedInfo.memories.videoDescription
                    ) ? (
                      <div
                        className="text-base leading-5 break-words md:text-lg text-default-800 text-left"
                        dangerouslySetInnerHTML={{
                          __html:
                            barcodeData.storedInfo.memories.videoDescription,
                        }}
                      />
                    ) : (
                      <p className="text-[16px] break-words text-left">
                        {barcodeData.storedInfo.memories.videoDescription
                          .split(/(\s+)/)
                          .map((word, index) => {
                            const urlRegex = /^(https?:\/\/[^\s]+)$/i;
                            if (urlRegex.test(word)) {
                              return (
                                <a
                                  key={index}
                                  href={word.toLowerCase()}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-blue-600 underline"
                                >
                                  {word}
                                </a>
                              );
                            }
                            return <span key={index}>{word}</span>;
                          })}
                      </p>
                    )}
                  </div>
                )}
                {barcodeData?.storedInfo?.memories?.videoUrl && (
                  <div className="space-y-4">
                    <div className="flex justify-center">
                      {/* Check if videoUrl contains 'youtube.com' or 'youtu.be' */}
                      {barcodeData?.storedInfo?.memories?.videoUrl.includes(
                        "youtube.com"
                      ) ||
                      barcodeData?.storedInfo?.memories?.videoUrl.includes(
                        "youtu.be"
                      ) ? (
                        <iframe
                          className="w-full rounded-md h-[300px] md:h-[400px]"
                          src={`https://www.youtube.com/embed/${
                            barcodeData?.storedInfo?.memories?.videoUrl?.split(
                              "/"
                            )[3]
                          }`}
                          title={`YouTube video player for ${barcodeData?.storedInfo?.memories?.videoUrl}`}
                          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                          allowFullScreen
                        />
                      ) : (
                        <div className=" py-4 text-[16px] text-center rounded-md">
                          {barcodeData?.storedInfo?.memories?.videoUrl ||
                            "Free text"}
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Video Section */}
                {barcodeData?.storedInfo?.memories?.videos?.length > 0 && (
                  <div className="mt-4 md:mt-8">
                    {barcodeData?.storedInfo?.memories?.videos?.map(
                      (video, index) => (
                        <div
                          key={index}
                          className="flex justify-center mb-6 sm:mb-8"
                        >
                          <video
                            className="w-full h-auto md:h-[400px] rounded-md"
                            controls
                            loop
                            playsInline
                            type={`video/${video?.url?.split(".").pop()}`}
                          >
                            <source
                              src={`${video?.url}#t=0.001`}
                              type="video/mp4"
                            />
                            Your browser does not support the video tag.
                          </video>
                        </div>
                      )
                    )}
                  </div>
                )}
              </div>
            )}
            {activeTab === "pdf" && (
              <div>
                {barcodeData?.storedInfo?.memories?.pdfTitle && (
                  <div className="pt-4 pb-2 rounded-md">
                    <p className="text-2xl font-medium break-words text-[16px]">
                      {barcodeData?.storedInfo?.memories?.pdfTitle}
                    </p>
                  </div>
                )}

                {/* PDF Section with Name and Button for each PDF */}
                {barcodeData?.storedInfo?.memories?.pdfs?.length > 0 && (
                  <div className="space-y-4 mt-4 md:mt-8">
                    {barcodeData?.storedInfo?.memories?.pdfs?.map(
                      (pdf, index) => (
                        <div
                          key={index}
                          className="flex justify-between items-center gap-2 rounded-md shadow-[1px_1px_16px_rgba(0,0,0,0.15)] p-3"
                        >
                          <span className="text-[16px] break-all">
                            {`${pdf?.split("/").pop()}`}
                          </span>
                          <button
                            onClick={() => openPdfModal(pdf)}
                            className="bg-[#4E7BBE] text-white py-1 px-3 rounded-md min-w-16"
                          >
                            Open
                          </button>
                        </div>
                      )
                    )}
                  </div>
                )}

                {isPdfOpen && pdfUrl ? (
                  <div className="fixed inset-0 z-50 flex items-center justify-center gap-2 bg-black bg-opacity-75 p-4">
                    <div className="relative w-full max-w-4xl py-4 rounded-lg">
                      {/* Close Icon */}
                      <button
                        onClick={closePdfModal}
                        className="absolute top-4 right-4 bg-black bg-opacity-50 text-white rounded-full text-xl w-6 h-6 flex items-center justify-center leading-none hover:bg-opacity-75 transition-opacity z-10"
                      >
                        <IoClose />
                      </button>

                      {/* Loading Indicator */}
                      {pdfLoading && !pdfError && (
                        <div className="absolute inset-0 flex items-center justify-center bg-white rounded-lg">
                          <div className="text-center">
                            <div className="h-12 w-12 mx-auto animate-spin rounded-full border-4 border-solid border-blue-500 border-r-transparent"></div>
                            <p className="mt-4 text-gray-600">Loading PDF...</p>
                          </div>
                        </div>
                      )}

                      {/* Error Message */}
                      {pdfError && (
                        <div className="absolute inset-0 flex items-center justify-center bg-white rounded-lg p-8">
                          <div className="text-center">
                            <p className="text-red-600 mb-4">
                              Failed to load PDF viewer after {pdfRetryCount}{" "}
                              attempts.
                            </p>
                            <div className="space-x-4">
                              {pdfRetryCount < 3 && (
                                <button
                                  onClick={retryPdfLoad}
                                  className="inline-block bg-green-600 text-white py-2 px-4 rounded-md hover:bg-green-700 transition-colors"
                                >
                                  Retry Loading
                                </button>
                              )}
                              <a
                                href={pdfUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-block bg-blue-600 text-white py-2 px-4 rounded-md hover:bg-blue-700 transition-colors"
                              >
                                Open PDF in New Tab
                              </a>
                            </div>
                          </div>
                        </div>
                      )}

                      {/* PDF Iframe */}
                      <div
                        className={
                          pdfLoading || pdfError ? "invisible" : "visible"
                        }
                      >
                        <iframe
                          key={iframeKey}
                          className="w-full h-[90vh] rounded-lg"
                          src={`https://docs.google.com/viewer?url=${encodeURIComponent(
                            pdfUrl
                          )}&embedded=true&hl=en`}
                          onLoad={(e) => {
                            // Additional check to ensure content is actually loaded
                            setTimeout(() => {
                              try {
                                const iframe = e.target;
                                if (iframe && iframe.contentWindow) {
                                  setPdfLoading(false);
                                  setPdfError(false);
                                } else {
                                  retryPdfLoad();
                                }
                              } catch (error) {
                                console.warn("PDF load check failed:", error);
                                setPdfLoading(false);
                                setPdfError(false); // Assume it loaded if we can't check
                              }
                            }, 2000); // Wait 2 seconds to check if content loaded
                          }}
                          onError={() => {
                            console.warn("PDF iframe error occurred");
                            retryPdfLoad();
                          }}
                          frameBorder="0"
                          id={`myiframe-${iframeKey}`}
                          style={{
                            height: "90vh",
                            width: "100%",
                            backgroundColor: "white",
                          }}
                          title="PDF Viewer"
                          allow="autoplay"
                          sandbox="allow-scripts allow-same-origin allow-popups allow-forms"
                        ></iframe>
                      </div>
                    </div>
                  </div>
                ) : null}
              </div>
            )}
          </div>

          {/* Loading State */}
          {processing ? (
            <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-40 z-50">
              <div
                className="h-10 w-10 animate-spin rounded-full border-4 border-solid border-current border-r-transparent"
                role="status"
              >
                <span className="sr-only">Loading...</span>
              </div>
            </div>
          ) : null}
        </div>
        {/* Footer */}
        <div className="bg-[#E7EEF5] px-4 py-4 md:py-6 mt-[50px] md:mt-[80px] lg:mt-[100px]">
          <div className="max-w-[800px] w-full mx-auto flex items-center gap-2 md:gap-4 flex-col sm:flex-row justify-between">
            <div className="flex flex-col items-center gap-1 md:gap-2 md:max-w-[190px] w-full">
              <p className="text-sm md:text-base font-normal text-center">
                Create a memorial like this for someone you love
              </p>
              <a
                href="https://foreverconnected.store/"
                target="_blank"
                rel="noopener noreferrer"
                className=""
              >
                <img
                  src={ForeverConnectedLogo}
                  alt="Forever Connected Logo"
                  className="max-w-[150px] md:max-w-[170px] w-full mx-auto mb-1"
                />
              </a>
            </div>
            <div className="flex flex-col items-center gap-1 md:gap-2 md:max-w-[250px] w-full">
              <p className="text-sm md:text-base font-normal text-center">
                Connecting brands and people through artistic QR codes
              </p>
              <a
                href="https://rainbowrebels.store/"
                target="_blank"
                rel="noopener noreferrer"
                className=""
              >
                <img
                  src={RainbowLogo}
                  alt="Rainbow Rebels Logo"
                  className="max-w-[150px] md:max-w-[170px] w-full mx-auto mb-1"
                />
              </a>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default BarcodeInfo;
